import React, { useCallback, useEffect, useMemo, useState } from 'react';
import zohoService from '../../../services/zohoService';
import { toast } from 'react-toastify';

const PAGE_SIZE = 5;

const formatDateTime = (iso) => {
  if (!iso) {
    return '—';
  }

  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Bahrain',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const quotaBarVariant = (used, limit) => {
  const ratio = used / limit;
  if (ratio >= 0.9) {
    return 'bg-danger';
  }
  if (ratio >= 0.7) {
    return 'bg-warning';
  }
  return 'bg-primary';
};

const StatTile = ({ label, value, variant = 'secondary' }) => (
  <div className="col-6 col-md-3">
    <div className="border rounded px-2 py-2 text-center h-100">
      <div className={`fw-semibold ${variant === 'danger' ? 'text-danger' : ''} ${variant === 'warning' ? 'text-warning' : ''}`}>
        {value}
      </div>
      <div className="small text-muted">{label}</div>
    </div>
  </div>
);

const PaginationBar = ({ page, total, pageSize, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  if (total === 0) {
    return null;
  }

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <div className="d-flex align-items-center flex-wrap gap-2 mt-3">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        Prev
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline-secondary'}`}
          onClick={() => onPage(p)}
        >
          {p}
        </button>
      ))}
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        disabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      >
        Next
      </button>
      <span className="small text-muted ms-auto">
        {start}–{end} of {total}
      </span>
    </div>
  );
};

const ZohoSyncQueueCard = () => {
  const [status, setStatus] = useState(null);
  const [failures, setFailures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [isRetryingAll, setIsRetryingAll] = useState(false);
  const [page, setPage] = useState(1);

  const loadData = useCallback(async () => {
    try {
      const [statusData, failuresData] = await Promise.all([
        zohoService.getSyncStatus(),
        zohoService.getSyncFailures(),
      ]);
      setStatus(statusData);
      setFailures(failuresData?.data ?? []);
    } catch (error) {
      toast.error(error.message || 'Failed to load sync queue', {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  const sortedFailures = useMemo(
    () => [...failures].sort(
      (a, b) => new Date(b.last_failed_at || 0) - new Date(a.last_failed_at || 0),
    ),
    [failures],
  );

  const totalPages = Math.max(1, Math.ceil(sortedFailures.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pageFailures = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sortedFailures.slice(start, start + PAGE_SIZE);
  }, [sortedFailures, page]);

  const handleRetry = async (failureId) => {
    setRetryingId(failureId);
    try {
      const result = await zohoService.retrySync(failureId);
      if (result?.success) {
        toast.success('Sync succeeded', { position: 'top-right', autoClose: 2000 });
      } else {
        toast.error(result?.error || 'Sync failed', { position: 'top-right', autoClose: 4000 });
      }
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Retry failed', { position: 'top-right', autoClose: 3000 });
    } finally {
      setRetryingId(null);
    }
  };

  const handleRetryAll = async () => {
    setIsRetryingAll(true);
    try {
      const result = await zohoService.retryAllSync();
      const message = `Sync All: ${result.succeeded ?? 0} succeeded, ${result.failed ?? 0} failed`;
      if (result.stopped_reason === 'rate_limit') {
        toast.warning(`${message} (stopped: rate limit)`, { position: 'top-right', autoClose: 5000 });
      } else if ((result.failed ?? 0) > 0) {
        toast.warning(message, { position: 'top-right', autoClose: 4000 });
      } else {
        toast.success(message, { position: 'top-right', autoClose: 3000 });
      }
      await loadData();
    } catch (error) {
      toast.error(error.message || 'Sync All failed', { position: 'top-right', autoClose: 3000 });
    } finally {
      setIsRetryingAll(false);
    }
  };

  const failureCount = status?.open_failure_count ?? failures.length;
  const apiUsed = status?.api_used_today ?? 0;
  const dailyLimit = status?.daily_limit ?? 5000;
  const quotaPercent = Math.min(100, Math.round((apiUsed / dailyLimit) * 100));
  const rateLimited = status?.rate_limited ?? false;

  return (
    <div className="card mb-3">
      <div className="card-header d-flex align-items-center justify-content-between py-2">
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <h6 className="mb-0">Failed syncs ({failureCount})</h6>
          <span className="badge text-bg-light text-dark border">Latest first</span>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={isLoading || isRetryingAll || failureCount === 0}
          onClick={handleRetryAll}
        >
          {isRetryingAll ? 'Syncing…' : 'Sync All'}
        </button>
      </div>

      <div className="card-body py-3">
        {isLoading ? (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="row g-2 mb-3">
              <StatTile label="Open failures" value={failureCount} variant="danger" />
              <StatTile label="API used today" value={apiUsed.toLocaleString()} />
              <StatTile label="Daily limit" value={dailyLimit.toLocaleString()} />
              <StatTile label="Rate limited" value={rateLimited ? 'Yes' : 'No'} variant={rateLimited ? 'warning' : 'secondary'} />
            </div>

            <div className="mb-2 d-flex justify-content-between small">
              <span>Zoho API quota</span>
              <span className={rateLimited ? 'text-danger fw-semibold' : ''}>
                {apiUsed.toLocaleString()} / {dailyLimit.toLocaleString()}
                {rateLimited ? ' (rate limited)' : ''}
              </span>
            </div>
            <div className="progress mb-2" style={{ height: 6 }}>
              <div
                className={`progress-bar ${quotaBarVariant(apiUsed, dailyLimit)}`}
                role="progressbar"
                style={{ width: `${quotaPercent}%` }}
                aria-valuenow={apiUsed}
                aria-valuemin={0}
                aria-valuemax={dailyLimit}
              />
            </div>
            <p className="text-muted small mb-3">
              Last successful push: {formatDateTime(status?.last_success_at)}
              {' · '}
              Auto-retry paused for failed items until you sync or edit the source record.
            </p>

            {rateLimited && (
              <div className="alert alert-warning py-2 small mb-3" role="alert">
                Daily API limit or code 45 detected in today&apos;s log — manual sync may stop early.
              </div>
            )}

            {sortedFailures.length === 0 ? (
              <p className="text-muted small mb-0">No failed syncs — queue is clear.</p>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ whiteSpace: 'nowrap' }}>Latest</th>
                        <th>Entity</th>
                        <th>Reference</th>
                        <th style={{ minWidth: 180 }}>Error</th>
                        <th className="text-end" style={{ width: 72 }}>Sync</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageFailures.map((failure) => (
                          <tr key={failure.id}>
                            <td className="small text-muted text-nowrap">
                              {formatDateTime(failure.last_failed_at)}
                            </td>
                            <td>
                              <span className="badge text-bg-secondary">
                                {failure.entity_label || failure.entity_type}
                              </span>
                              {failure.attempt_count > 1 && (
                                <span className="ms-1 small text-muted">×{failure.attempt_count}</span>
                              )}
                            </td>
                            <td className="fw-medium">{failure.reference}</td>
                            <td className="small" style={{ maxWidth: 320 }}>
                              <div
                                className="text-danger"
                                title={failure.error_code ? `Code ${failure.error_code}` : undefined}
                              >
                                {failure.error_message}
                              </div>
                              {failure.suggested_fix ? (
                                <div className="text-muted text-truncate" title={failure.suggested_fix}>
                                  Fix: {failure.suggested_fix}
                                </div>
                              ) : null}
                            </td>
                            <td className="text-end text-nowrap">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={retryingId === failure.id || isRetryingAll}
                                onClick={() => handleRetry(failure.id)}
                              >
                                {retryingId === failure.id ? '…' : 'Sync'}
                              </button>
                            </td>
                          </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <PaginationBar
                  page={page}
                  total={sortedFailures.length}
                  pageSize={PAGE_SIZE}
                  onPage={setPage}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ZohoSyncQueueCard;
